'use client'

import { useState } from 'react'
import { ColumnDef } from "@tanstack/react-table"
import { 
  ArrowUpDown, 
  MoreHorizontal, 
  Trash, 
  Loader2, 
  Building2, 
  ExternalLink, 
  Edit,
  Users,
  Mail,
  Phone,
  Globe,
  MapPin,
  Calendar
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { EditCompanyForm } from "./EditCompanyForm"
import FormModal from "@/components/FormModal"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import CompanyService from '@/api/companies.service'
import { useToast } from '@/hooks/use-toast'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogFooter, AlertDialogDescription, AlertDialogTitle, AlertDialogContent, AlertDialogHeader } from '@/components/ui/alert-dialog'

// This type is used to define the shape of our data.
export type Company = {
  id: number;
  publicId: string;
  name: string;
  emailAddress: string;
  phoneNumber: string;
  address: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  website: string;
  timezone: string;
  dateUpdated: string;
  dateCreated: string;
}

export const columns: ColumnDef<Company>[] = [
  {
    accessorKey: "name",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white"
      >
        Name
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ cell }) => (
      <div className="flex items-center">
        <Building2 className="w-4 h-4 mr-2 text-slate-400" />
        <span className="font-medium text-slate-900 dark:text-slate-100">
          {cell.row.original.name}
        </span>
      </div>
    ),
  },
  {
    accessorKey: "emailAddress",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white whitespace-nowrap"
      >
        <Mail className="w-4 h-4 mr-2" />
        Email
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ cell }) => {
      const email = cell.getValue() as string;
      
      if (!email) {
        return <span className="text-slate-500">—</span>;
      }
      
      return (
        <div className="flex items-center">
          <Mail className="w-4 h-4 mr-2 text-slate-400" />
          <a 
            href={`mailto:${email}`} 
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
          >
            {email}
          </a>
        </div>
      );
    },
  },
  {
    accessorKey: "phoneNumber",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white whitespace-nowrap"
      >
        <Phone className="w-4 h-4 mr-2" />
        Phone
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ cell }) => {
      const phone = cell.getValue() as string;
      
      if (!phone) {
        return <span className="text-slate-500">—</span>;
      }
      
      return (
        <div className="flex items-center">
          <Phone className="w-4 h-4 mr-2 text-slate-400" />
          <a 
            href={`tel:${phone}`} 
            className="text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100"
          >
            {phone}
          </a>
        </div>
      );
    },
  },
  {
    accessorKey: "location",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white whitespace-nowrap"
      >
        <MapPin className="w-4 h-4 mr-2" />
        Location
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ row }) => {
      const city = row.original.city;
      const country = row.original.country;
      
      if (!city && !country) {
        return <span className="text-slate-500">—</span>;
      }
      
      return (
        <div className="text-slate-700 dark:text-slate-300">
          {city && country ? `${city}, ${country}` : city || country}
        </div>
      );
    },
  },
  {
    accessorKey: "website",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white whitespace-nowrap"
      >
        <Globe className="w-4 h-4 mr-2" />
        Website
      </Button>
    ),
    cell: ({ cell }) => {
      const website = cell.getValue() as string;
      
      if (!website) {
        return <span className="text-slate-500">—</span>;
      }
      
      return (
        <a 
          href={website.startsWith('http') ? website : `https://${website}`} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 hover:underline flex items-center"
        >
          <Globe className="w-4 h-4 mr-2 text-slate-400" />
          {website.replace(/^https?:\/\//, '')}
        </a>
      );
    },
  },
  {
    accessorKey: "dateCreated",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white whitespace-nowrap"
      >
        <Calendar className="w-4 h-4 mr-2" />
        Created
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ cell }) => {
      const date = new Date(cell.getValue() as string);
      const formattedDate = `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
      const formattedTime = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
      
      return (
        <div className="text-slate-700 dark:text-slate-300">
          <div className="font-medium">{formattedDate}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">{formattedTime}</div>
        </div>
      );
    },
  },
  {
    id: "actions",
    cell: ({ row }) => {
      const { toast } = useToast();
      const [isModalOpen, setModalOpen] = useState(false);
      const [showDeleteDialog, setShowDeleteDialog] = useState(false);
      const [isDeleting, setIsDeleting] = useState(false);

      const handleDelete = async () => {
        setIsDeleting(true);
        try {
          const response = await CompanyService.deleteCompany(row.original.publicId);
          if (response.data.result === "success") {
            toast({
              title: "Company Deleted",
              description: "Company has been successfully deleted",
              variant: "default",
            });
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          } else {
            toast({
              title: "Error",
              description: response.data.message || "Failed to delete company",
              variant: "destructive",
            });
          }
        } catch (error) {
          console.error(error);
          toast({
            title: "Error",
            description: "Failed to delete company",
            variant: "destructive",
          });
        } finally {
          setIsDeleting(false);
          setShowDeleteDialog(false);
        }
      };

      return (
        <div className="flex items-center justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700">
              <DropdownMenuLabel className="text-slate-700 dark:text-slate-300">Actions</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-slate-200 dark:bg-slate-700" />
              <DropdownMenuItem 
                className="text-slate-700 dark:text-slate-300 focus:bg-slate-100 dark:focus:bg-slate-800 cursor-pointer"
                onClick={() => window.location.href = `/company/${row.original.publicId}`}
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                <span>View Company</span>
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="text-slate-700 dark:text-slate-300 focus:bg-slate-100 dark:focus:bg-slate-800 cursor-pointer"
                onClick={() => setModalOpen(true)}
              >
                <Edit className="mr-2 h-4 w-4" />
                <span>Edit Company</span>
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="text-rose-600 dark:text-rose-500 focus:bg-rose-50 dark:focus:bg-rose-900/30 cursor-pointer"
                onClick={() => setShowDeleteDialog(true)}
              >
                <Trash className="mr-2 h-4 w-4" />
                <span>Delete Company</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <FormModal
            open={isModalOpen}
            setOpen={setModalOpen}
            title="Edit Company"
          >
            <EditCompanyForm company={row.original} />
          </FormModal>
          
          <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <AlertDialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-slate-900 dark:text-slate-100">Are you sure?</AlertDialogTitle>
                <AlertDialogDescription className="text-slate-600 dark:text-slate-400">
                  This will permanently delete the company "{row.original.name}". This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="border-slate-200 text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800">
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction 
                  onClick={(e) => {
                    e.preventDefault();
                    handleDelete();
                  }} 
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting...
                    </>
                  ) : (
                    <>
                      <Trash className="w-4 h-4 mr-2" /> Delete
                    </>
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      );
    }
  },
]
